import { put, call, select, takeEvery } from 'redux-saga/effects';

import { errMessage } from '../../lib/apiError';
import { findNodeContext } from '../../lib/taxonomy';
import { selectTaxonomyTree } from '../selectors/taxonomy';
import {
  moveNode,
  fetchTree,
  createNode,
  deleteNode,
  updateNode,
  reorderNode,
  fetchAttributes,
} from '../apis/taxonomy';
import {
  selectTaxonomyNode,
  taxonomyNodePatched,
  getTaxonomyTreeAction,
  getTaxonomyTreeSuccess,
  getTaxonomyTreeFailure,
  moveTaxonomyNodeAction,
  taxonomyMutationSuccess,
  taxonomyMutationFailure,
  createTaxonomyNodeAction,
  updateTaxonomyNodeAction,
  deleteTaxonomyNodeAction,
  reorderTaxonomyNodeAction,
  getAttributeDefinitionsAction,
  getAttributeDefinitionsSuccess,
  getAttributeDefinitionsFailure,
} from '../slices/taxonomy';

function* getTree(action) {
  try {
    const response = yield call(fetchTree, action.payload);
    yield put(getTaxonomyTreeSuccess(response));
  } catch (error) {
    yield put(getTaxonomyTreeFailure(errMessage(error, 'Failed to load the taxonomy tree')));
  }
}

function* getAttributeDefinitions(action) {
  try {
    const response = yield call(fetchAttributes, action.payload);
    yield put(getAttributeDefinitionsSuccess(response));
  } catch (error) {
    yield put(
      getAttributeDefinitionsFailure(errMessage(error, 'Failed to load attribute definitions'))
    );
  }
}

function* handleCreateNode(action) {
  try {
    const created = yield call(createNode, action.payload);
    yield put(taxonomyMutationSuccess({ selectId: created?.id }));
    // Refetch: an insert renumbers sort_order across the new node's siblings.
    yield put(getTaxonomyTreeAction());
  } catch (error) {
    yield put(taxonomyMutationFailure(errMessage(error, 'Failed to create the node')));
  }
}

function* handleUpdateNode(action) {
  try {
    const { id, data } = action.payload;
    const updated = yield call(updateNode, id, data);
    const tree = yield select(selectTaxonomyTree);
    const current = findNodeContext(tree, id)?.node;
    // A field edit patches in place; image_url/icon_url are tree-only enrichments,
    // so swapping an asset still needs the refetch to resolve the new URL.
    const assetsChanged =
      (data.image_asset_id || null) !== (current?.image_asset_id || null) ||
      (data.icon_asset_id || null) !== (current?.icon_asset_id || null);
    yield put(taxonomyNodePatched({ ...(updated?.data || updated), id }));
    yield put(taxonomyMutationSuccess());
    if (assetsChanged) yield put(getTaxonomyTreeAction());
  } catch (error) {
    yield put(taxonomyMutationFailure(errMessage(error, 'Failed to update the node')));
  }
}

function* handleDeleteNode(action) {
  try {
    yield call(deleteNode, action.payload);
    yield put(taxonomyMutationSuccess());
    yield put(selectTaxonomyNode(null));
    yield put(getTaxonomyTreeAction());
  } catch (error) {
    yield put(taxonomyMutationFailure(errMessage(error, 'Failed to delete the node')));
  }
}

function* handleReorderNode(action) {
  try {
    const { id, afterId } = action.payload;
    yield call(reorderNode, id, afterId);
    yield put(taxonomyMutationSuccess());
    // Refetch: the server renumbers sort_order across every sibling, so a targeted patch would lie.
    yield put(getTaxonomyTreeAction());
  } catch (error) {
    yield put(taxonomyMutationFailure(errMessage(error, 'Failed to reorder the node')));
  }
}

// A cross-parent drop is a move plus a position; the contract splits them.
function* handleMoveNode(action) {
  try {
    const { id, parentId, afterId } = action.payload;
    yield call(moveNode, id, parentId);
    if (afterId !== undefined) yield call(reorderNode, id, afterId);
    yield put(taxonomyMutationSuccess({ selectId: id }));
    // Refetch: a move renumbers sort_order in both the old and the new sibling list.
    yield put(getTaxonomyTreeAction());
  } catch (error) {
    yield put(taxonomyMutationFailure(errMessage(error, 'Failed to move the node')));
  }
}

export default function* taxonomySaga() {
  yield takeEvery(getTaxonomyTreeAction.type, getTree);
  yield takeEvery(getAttributeDefinitionsAction.type, getAttributeDefinitions);
  yield takeEvery(createTaxonomyNodeAction.type, handleCreateNode);
  yield takeEvery(updateTaxonomyNodeAction.type, handleUpdateNode);
  yield takeEvery(deleteTaxonomyNodeAction.type, handleDeleteNode);
  yield takeEvery(reorderTaxonomyNodeAction.type, handleReorderNode);
  yield takeEvery(moveTaxonomyNodeAction.type, handleMoveNode);
}
